/**
 * @file ImportTemplateList.js
 * @description Displays a list of Custom Imports with their details and provides functionality to select, add, edit, and delete templates.
 */

import { useState, useMemo, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './ImportTemplates.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import style from './ImportTemplates.module.scss';
import { LogEventType, SecurityAttributes } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ImportTemplateList = ({
    templates,
    onAddEditTemplate,
    onDeleteSelectedTemplates,
    onCopySelectedTemplates,
}) => {
    const { showModal } = useContext(ModalContext);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const selectAllCheckboxRef = useRef(null);

    const { hasSecurityAttribute } = useAuthContext();

    const hasFullAccess = hasSecurityAttribute([
        SecurityAttributes.CustomImportsFullAccess,
    ]);

    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate =
                selectedTemplateIds.length > 0 &&
                selectedTemplateIds.length < templates.length;
        }
    }, [selectedTemplateIds, templates]);

    const sortedTemplates = useMemo(() => {
        return [...templates].sort((a, b) => {
            let aValue;
            let bValue;

            if (sortConfig.key === 'type') {
                aValue = getTemplateImportTypeLabel(a);
                bValue = getTemplateImportTypeLabel(b);
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [templates, sortConfig]);

    const handleSort = key => {
        setSortConfig({
            key,
            direction:
                sortConfig.key === key &&
                sortConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        });
    };

    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className={`px-2 ${style.sortArrowIcon}`}>
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const getTemplateImportTypeLabel = template => {
        const explicitType = (
            template?.type ||
            template?.importType ||
            ''
        ).trim();

        if (explicitType.length > 0) {
            return explicitType;
        }

        if (template?.importTypeId === 0) {
            return getText('msg_09cb6197bf14');
        }

        return '';
    };

    const handleCheckboxChange = (templateId, isChecked) => {
        setSelectedTemplateIds(
            isChecked
                ? [...selectedTemplateIds, templateId]
                : selectedTemplateIds.filter(id => id !== templateId),
        );
    };

    const handleSelectAll = isChecked => {
        setSelectedTemplateIds(
            isChecked ? templates.map(template => template.id) : [],
        );
    };

    const handleDelete = () => {
        onDeleteSelectedTemplates(selectedTemplateIds);
        setSelectedTemplateIds([]);
    };

    const isTemplateSelected = id => selectedTemplateIds.includes(id);

    const openDeleteConfirmationModal = () => {
        if (!selectedTemplateIds.length) {
            Toast.error(getText('msg_c8ccc5b97e0d'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            onYes: () => handleDelete(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Import template delete clicked`);
    };

    const duplicateTemplate = () => {
        if (!selectedTemplateIds.length) {
            Toast.error(getText('msg_b244a580f588'));
            return;
        }
        onCopySelectedTemplates(selectedTemplateIds);
        setSelectedTemplateIds([]);
    };

    return (
        <div className="import-template-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <p className="page-title float-start card-title ms-4">
                            {getText('msg_d13718d3a673')}
                        </p>

                        <div className="float-end d-flex align-items-center">
                            {hasFullAccess && (
                                <>
                                    <span
                                        type="button"
                                        onClick={openDeleteConfirmationModal}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-trash3 me-2" />
                                        {getText('msg_f6fdbe48dc54')}
                                    </span>
                                    <span
                                        type="button"
                                        onClick={duplicateTemplate}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-copy me-2" />
                                        {getText('msg_972d57379db3')}
                                    </span>
                                    <div to="new-portal-page">
                                        <span
                                            onClick={() =>
                                                onAddEditTemplate('')
                                            }
                                            type="button"
                                            className="ms-3 me-4 button-base ing-button-primary-left"
                                        >
                                            <i className="bi bi-plus-lg me-1" />
                                            {getText('msg_61cc55aa0453')}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={`table-container ${style.tableContainer}`}>
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    {hasFullAccess && (
                                        <th
                                            className={`text-center ${style.checkboxColumnHead}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className={`form-check-input`}
                                                ref={selectAllCheckboxRef}
                                                onChange={e =>
                                                    handleSelectAll(
                                                        e.target.checked,
                                                    )
                                                }
                                                checked={
                                                    selectedTemplateIds.length ===
                                                    sortedTemplates.length
                                                }
                                            />
                                        </th>
                                    )}
                                    <th
                                        className="table-column-header-text"
                                        onClick={() => handleSort('name')}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_7aa3610be381')}{' '}
                                            {renderSortArrow('name')}
                                        </span>
                                    </th>
                                    <th
                                        className="table-column-header-text"
                                        onClick={() => handleSort('type')}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_78cfb1f529c1')}{' '}
                                            {renderSortArrow('type')}
                                        </span>
                                    </th>
                                    <th
                                        className="table-column-header-text"
                                        onClick={() => handleSort('fileFormat')}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_5dac61882da9')}{' '}
                                            {renderSortArrow('fileFormat')}
                                        </span>
                                    </th>
                                    <th
                                        className="table-column-header-text"
                                        onClick={() => handleSort('delimiter')}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_5e96ff2a317d')}{' '}
                                            {renderSortArrow('delimiter')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTemplates.map(template => (
                                    <tr key={template.id}>
                                        {hasFullAccess && (
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={isTemplateSelected(
                                                        template.id,
                                                    )}
                                                    onChange={e =>
                                                        handleCheckboxChange(
                                                            template.id,
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            </td>
                                        )}
                                        <td
                                            className="item-page-name titleCell"
                                            onClick={() =>
                                                onAddEditTemplate(template.id)
                                            }
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    onAddEditTemplate(
                                                        template.id,
                                                    );
                                                }
                                            }}
                                        >
                                            {template.name}
                                        </td>
                                        <td>
                                            {getTemplateImportTypeLabel(
                                                template,
                                            )}
                                        </td>
                                        <td>{template.fileFormat}</td>
                                        <td>{template.delimiter}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-center fs-3">
                            {sortedTemplates.length <= 0
                                ? getText('msg_67bdaea32434')
                                : ''}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

ImportTemplateList.propTypes = {
    templates: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.string,
            importTypeId: PropTypes.number,
            fileFormat: PropTypes.string.isRequired,
            delimiter: PropTypes.string,
        }),
    ).isRequired,
    onAddEditTemplate: PropTypes.func.isRequired,
    onDeleteSelectedTemplates: PropTypes.func.isRequired,
    onCopySelectedTemplates: PropTypes.func.isRequired,
};

export default ImportTemplateList;
